import { useParams, Link, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowLeft, Check, Leaf, Droplets, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getProductById } from '../data/products';

const WHATSAPP_NUMBER = "919847023246";

const BenefitsList = ({ benefits }) => {
  if (!benefits || benefits.length === 0) return null;
  
  const items = [];
  for (let i = 0; i < benefits.length; i++) {
    items.push(
      <li key={i} className="flex items-start gap-3">
        <div className="w-6 h-6 rounded-full bg-[#2D4A3E]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Check className="w-4 h-4 text-[#2D4A3E]" />
        </div>
        <span className="text-[#57534E]">{benefits[i]}</span>
      </li>
    );
  }
  return <ul className="space-y-4">{items}</ul>;
};

const IngredientsList = ({ ingredients }) => {
  if (!ingredients || ingredients.length === 0) return null;
  
  const items = [];
  for (let i = 0; i < ingredients.length; i++) {
    items.push(
      <li key={i} className="flex items-center gap-3 text-[#57534E]">
        <div className="w-2 h-2 rounded-full bg-[#C69C6D]"></div>
        {ingredients[i]}
      </li>
    );
  }
  return <ul className="space-y-3">{items}</ul>;
};

export const ProductDetail = () => {
  const { id } = useParams();
  const product = getProductById(id);

  // Scroll to top when component mounts or id changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!product) {
    return <Navigate to="/" replace />;
  }

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=` + encodeURIComponent("Hi! I'm interested in " + product.name);

  return (
    <div data-testid="product-detail-page" className="min-h-screen bg-[#FAF9F6] pt-24">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <Link 
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-[#57534E] hover:text-[#2D4A3E] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>
      </div>

      {/* Product Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Product Visual */}
          <div className="lg:col-span-5">
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-[#FAF9F6] to-[#F2F0E9] border border-[#E7E5E4]">
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              </div>
              {product.size && (
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/80 rounded-full text-sm font-medium">
                  {product.size}
                </span>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="lg:col-span-7 space-y-8">
            <div>
              <span className="inline-block px-4 py-2 bg-[#2D4A3E]/10 text-[#2D4A3E] text-sm font-medium rounded-full mb-4">
                {product.category}
              </span>
              <h1 data-testid="product-title" className="font-heading text-4xl md:text-5xl font-bold text-[#1C1C1C] mb-4">
                {product.name}
              </h1>
              <p className="text-xl text-[#C69C6D] font-medium mb-4">{product.tagline}</p>
              <p className="text-lg text-[#57534E] leading-relaxed">{product.description}</p>
            </div>

            {/* Benefits */}
            <div className="bg-white rounded-2xl p-8 border border-[#E7E5E4]">
              <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mb-6 flex items-center gap-3">
                <Check className="w-6 h-6 text-[#2D4A3E]" />
                Key Benefits
              </h3>
              <BenefitsList benefits={product.benefits} />
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button 
                  data-testid="product-inquiry-btn"
                  className="bg-[#2D4A3E] text-white hover:bg-[#1F352B] rounded-full px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-[#2D4A3E]/20 hover:-translate-y-1"
                >
                  Enquire Now
                </Button>
              </Link>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="product-whatsapp-btn"
                  variant="outline"
                  className="border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-8 py-6 text-lg transition-all duration-300"
                >
                  WhatsApp Inquiry
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Information */}
      <section className="bg-[#F2F0E9] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Ingredients */}
            <div className="bg-white rounded-2xl p-8 border border-[#E7E5E4]">
              <div className="w-14 h-14 rounded-xl bg-[#2D4A3E]/10 flex items-center justify-center mb-6">
                <Leaf className="w-7 h-7 text-[#2D4A3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mb-4">Ingredients</h3>
              <IngredientsList ingredients={product.ingredients} />
            </div>

            {/* How to Use */}
            <div className="bg-white rounded-2xl p-8 border border-[#E7E5E4]">
              <div className="w-14 h-14 rounded-xl bg-[#2D4A3E]/10 flex items-center justify-center mb-6">
                <Droplets className="w-7 h-7 text-[#2D4A3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mb-4">How to Use</h3>
              <p data-testid="product-usage" className="text-[#57534E] leading-relaxed">{product.usage}</p>
            </div>

            {/* Dosage */}
            <div className="bg-white rounded-2xl p-8 border border-[#E7E5E4]">
              <div className="w-14 h-14 rounded-xl bg-[#2D4A3E]/10 flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-[#2D4A3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-[#1C1C1C] mb-4">Recommended Dosage</h3>
              <p data-testid="product-dosage" className="text-[#57534E] leading-relaxed">{product.dosage}</p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 p-6 bg-[#FEF3C7] rounded-xl flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-[#92400E] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#92400E]">
              <strong>Disclaimer:</strong> This product is not intended to diagnose, treat, cure, or prevent any disease. 
              Please consult a qualified Ayurvedic practitioner before use. Keep out of reach of children. 
              Store in a cool, dry place away from direct sunlight.
            </p>
          </div>
        </div>
      </section>

      {/* Back to Products CTA */}
      <section className="py-16 md:py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-8">
            Explore Our Other Products
          </h2>
          <Link to="/">
            <Button 
              data-testid="view-all-products-btn"
              className="bg-[#2D4A3E] text-white hover:bg-[#1F352B] rounded-full px-10 py-6 text-lg transition-all duration-300 shadow-lg hover:-translate-y-1"
            >
              View All Products
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
